/*
 * Guided spotlight (sections/gn-spotlight.liquid).
 *
 * Dims the page, cuts a hole around a target element, and floats an explainer
 * card beside it — a coachmark, not a modal. The point is that the highlighted
 * control stays USABLE: the hole takes no pointer events, so a click inside it
 * reaches the real button underneath, and clicking the target advances the tour.
 * An overlay that only says "click here" while blocking the click is a
 * screenshot with extra steps.
 *
 * Targets are CSS selectors from theme settings, so this works anywhere in the
 * theme. On the product page it points at <giftnote-button>, which hydrates
 * late (~1.1s measured) — hence the wait for real GEOMETRY rather than for the
 * element to merely exist. A step whose target never appears is skipped, and if
 * no step resolves nothing is shown at all: a spotlight highlighting empty space
 * is worse than none.
 */
(() => {
  'use strict';

  /* One tour per host, not just the first on the page.
     A second gn-spotlight section (the cart drawer's) now renders alongside the
     product one, and each section emits its own <script> tag — so this IIFE
     runs twice and used to bind the same first host both times, leaving the
     second tour in the DOM with no code behind it. KEY already includes the
     section id, so the instances keep separate seen-flags. */
  function setup(host) {
    if (host.dataset.gnSpotReady) return;
    host.dataset.gnSpotReady = '1';

  const cfg = JSON.parse(host.dataset.gnSpotlight || '{}');
  const steps = [...host.querySelectorAll('[data-gn-spot-step]')];
  if (!steps.length) return;

  const KEY = 'gn-spotlight:' + (cfg.id || 'default');

  /* Frequency. `once` persists, `session` resets when the tab closes, `always`
     is for working on it in the theme editor. Storage can throw in private
     modes, so every access is guarded — failing to read a flag must not stop
     the tour, and failing to write one must not break the close. */
  function seen() {
    if (cfg.frequency === 'always') return false;
    try {
      const store = cfg.frequency === 'session' ? sessionStorage : localStorage;
      return store.getItem(KEY) === '1';
    } catch {
      return false;
    }
  }

  function remember() {
    if (cfg.frequency === 'always') return;
    try {
      (cfg.frequency === 'session' ? sessionStorage : localStorage).setItem(KEY, '1');
    } catch {
      /* private mode — the tour simply shows again next time */
    }
  }

  /* Blanket off-switch, for automated runs and for working in the theme editor.
     Suppressing tours by their individual storage keys is not practical from a
     test: the key carries the section id, which is generated per theme. */
  try {
    if (sessionStorage.getItem('gn-spotlight:off') === '1') return;
  } catch {
    /* private mode — carry on and show the tour */
  }

  if (seen()) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches && cfg.skipReducedMotion) return;

  /* Resolve a selector to an element that actually occupies space. Presence is
     not enough: a custom element exists in the DOM long before it renders, and
     a hole drawn around a 0x0 box lands in the top-left corner. */
  function waitForTarget(selector, timeout = 8000) {
    const start = Date.now();
    return new Promise((resolve) => {
      const tick = () => {
        const el = selector ? document.querySelector(selector) : null;
        if (el) {
          const r = el.getBoundingClientRect();
          if (r.width > 4 && r.height > 4) return resolve(el);
        }
        if (Date.now() - start > timeout) return resolve(null);
        setTimeout(tick, 120);
      };
      tick();
    });
  }

  let root = null;
  let index = 0;
  let resolved = [];
  let raf = 0;

  function build() {
    root = document.createElement('div');
    root.className = 'gn-spot';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'false');
    root.setAttribute('aria-label', cfg.label || 'Guided tour');
    root.style.setProperty('--gn-spot-dim', String(cfg.dim ?? 0.62));
    root.innerHTML =
      '<div class="gn-spot__hole"></div>' +
      '<div class="gn-spot__ring"></div>' +
      '<div class="gn-spot__card"></div>';

    document.body.appendChild(root);
    return root;
  }

  function position() {
    const step = resolved[index];
    if (!step) return;
    const r = step.el.getBoundingClientRect();
    const pad = 8;
    const hole = root.querySelector('.gn-spot__hole');
    const ring = root.querySelector('.gn-spot__ring');
    const card = root.querySelector('.gn-spot__card');

    for (const box of [hole, ring]) {
      box.style.insetBlockStart = `${Math.round(r.top - pad)}px`;
      box.style.insetInlineStart = `${Math.round(r.left - pad)}px`;
      box.style.inlineSize = `${Math.round(r.width + pad * 2)}px`;
      box.style.blockSize = `${Math.round(r.height + pad * 2)}px`;
    }

    /* Placement: beside the target first, then below, then above.
     *
     * Stacking it above or below is what a tooltip usually does, but this card
     * is tall (it carries a square clip) and on a product page that put it
     * straight over Add to cart — hiding the very controls the tour is about.
     * To the side it clears the whole buy stack, and only falls back to the
     * vertical positions when neither flank has room. Every branch is checked
     * against the viewport, so no arrangement can push it off-screen. */
    const ch = card.offsetHeight || 200;
    const cw = card.offsetWidth || 360;
    const gap = 16;
    const edge = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left;
    let top;

    const fitsRight = r.right + gap + cw <= vw - edge;
    const fitsLeft = r.left - gap - cw >= edge;

    if (fitsRight || fitsLeft) {
      // Prefer whichever flank has more room, so a target near one edge does
      // not get a card jammed into the remaining sliver.
      const useRight = fitsRight && (!fitsLeft || vw - r.right >= r.left);
      left = useRight ? r.right + gap : r.left - gap - cw;
      // Vertically centred on the target, then clamped.
      top = r.top + r.height / 2 - ch / 2;
      top = Math.min(top, vh - ch - edge);
      top = Math.max(edge, top);
    } else {
      const below = r.bottom + gap;
      top = below + ch > vh - edge ? Math.max(edge, r.top - gap - ch) : below;
      left = r.left + r.width / 2 - cw / 2;
      left = Math.min(left, vw - cw - edge);
      left = Math.max(edge, left);
    }

    card.style.insetBlockStart = `${Math.round(top)}px`;
    card.style.insetInlineStart = `${Math.round(left)}px`;
  }

  function track() {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      const step = resolved[index];
      if (!step || !root) return;
      /* Scrolled past the target: close rather than leave the card pinned to a
         hole that is no longer on screen. Without this the overlay follows you
         down the page and sits over unrelated sections, still dimming
         everything, with nothing highlighted. */
      const r = step.el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) {
        finish();
        return;
      }
      position();
    });
  }

  function render() {
    const step = resolved[index];
    const card = root.querySelector('.gn-spot__card');
    card.replaceChildren(step.tpl.content.cloneNode(true));

    const foot = document.createElement('div');
    foot.className = 'gn-spot__foot';

    if (resolved.length > 1) {
      const dots = document.createElement('div');
      dots.className = 'gn-spot__dots';
      resolved.forEach((_, i) => {
        const d = document.createElement('span');
        d.className = 'gn-spot__dot';
        if (i === index) d.setAttribute('data-on', '');
        dots.appendChild(d);
      });
      foot.appendChild(dots);
    } else {
      foot.appendChild(document.createElement('span')).className = 'gn-spot__dots';
    }

    if (index > 0) {
      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'gn-spot__btn gn-spot__btn--ghost';
      back.textContent = 'Back';
      back.addEventListener('click', () => go(index - 1));
      foot.appendChild(back);
    }

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'gn-spot__btn gn-spot__btn--primary';
    next.textContent =
      index === resolved.length - 1 ? step.tpl.dataset.done || 'Got it' : step.tpl.dataset.next || 'Next';
    next.addEventListener('click', () => (index === resolved.length - 1 ? finish() : go(index + 1)));
    foot.appendChild(next);

    card.appendChild(foot);

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'gn-spot__close';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML =
      '<svg viewBox="0 0 20 20" width="14" height="14" fill="none" aria-hidden="true">' +
      '<path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
    close.addEventListener('click', finish);
    card.appendChild(close);

    position();
    requestAnimationFrame(() => card.setAttribute('data-shown', ''));
    next.focus({ preventScroll: true });
  }

  function go(i) {
    index = i;
    const card = root.querySelector('.gn-spot__card');
    card.removeAttribute('data-shown');
    resolved[index].el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    // Let the smooth scroll settle before measuring, or the hole is placed
    // against a rect that is still moving.
    setTimeout(render, 320);
  }

  function finish() {
    remember();
    window.removeEventListener('scroll', track, true);
    window.removeEventListener('resize', track);
    document.removeEventListener('keydown', onKey);
    document.removeEventListener('click', onOutside, true);
    if (root) root.remove();
    root = null;
  }

  function onKey(e) {
    if (e.key === 'Escape') finish();
  }

  function onOutside(e) {
    if (!root) return;
    // Clicks on the card drive the tour; anything else dismisses it. The event
    // is not cancelled, so the underlying control still does its job.
    if (e.target.closest && e.target.closest('.gn-spot__card')) return;
    finish();
  }

  async function start() {
    resolved = [];
    for (const tpl of steps) {
      // `wait` lets a tour sit patiently for something that only appears on an
      // interaction. The cart-drawer tour points at a panel that does not exist
      // until the shopper adds to cart, which is well past the 8s default.
      const el = await waitForTarget(tpl.dataset.target, Number(cfg.wait) || 8000);
      if (el) resolved.push({ tpl, el });
    }
    // Nothing to point at — say nothing rather than dim the page for no reason.
    if (!resolved.length) return;

    build();
    root.setAttribute('data-open', '');

    // Clicking the highlighted control is the goal, so treat it as completion
    // rather than leaving the overlay sitting over whatever it opens.
    resolved.forEach(({ el }) => el.addEventListener('click', finish, { once: true }));

    window.addEventListener('scroll', track, true);
    window.addEventListener('resize', track);
    document.addEventListener('keydown', onKey);
    // Dismiss on a click anywhere off the card. This listens on the document
    // rather than on a full-screen catcher, so the click still reaches whatever
    // it landed on — including the highlighted control.
    document.addEventListener('click', onOutside, true);

    resolved[0].el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setTimeout(render, 340);
  }

  setTimeout(start, Number(cfg.delay) || 0);
  }

  document.querySelectorAll('[data-gn-spotlight]').forEach(setup);
})();
